import { useEffect, useRef, useState } from 'react';
import { FlatList, Modal, Platform, Pressable, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View, XStack, YStack } from 'tamagui';
import { ChevronLeft, ChevronRight, X } from 'lucide-react-native';

import { AuthenticatedImage } from '@/components/authenticated-image';
import { LinkPreviewCard } from '@/components/link-preview-card';
import { Text } from '@/components/text';
import type { Attachment } from '@/lib/api';
import { imageRows, photoCellLabel, positionLabel } from '@/lib/image-grid';
import { thumbnailPath } from '@/lib/photo-path';
import { darkColors, lightColors, radius } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

const GAP = 4;
const FILL_MAX = 520;
const TARGET = 44;

// Web-only focus ring for a photo cell: the browser's own default is a thin
// unthemed outline. react-native-web passes these through to CSS.
const focusRing = (focused: boolean | undefined) =>
  Platform.OS === 'web' && focused
    ? ({ outlineStyle: 'solid', outlineWidth: 2, outlineOffset: 2, outlineColor: lightColors.borderFocus } as object)
    : null;

type PressState = { pressed: boolean; hovered?: boolean; focused?: boolean };

// The one renderer for attachments, shared by every surface that carries
// them (design.md, "Rich content"): images and link-preview cards.
//
// Images are a grid (lib/image-grid.ts), not a stack, and each opens in a
// lightbox you can page through. Link cards follow below the photos.
//
// `preview` is the feed-card form: the first attachment only -- a wide strip
// for an image, a compact card for a link -- with a count badge over the
// image when there is more, so a card with rich content reads as visibly
// different from a text-only one without a stack of media turning the list
// into a wall.
export function AttachmentList({
  attachments,
  width = 220,
  preview = false,
}: {
  attachments: Attachment[];
  // A number is a fixed pixel width (chat bubbles shrink-wrap their
  // content, so they need one); 'fill' takes the whole column up to
  // FILL_MAX, for thread and reply bodies that have a column to fill.
  width?: number | 'fill';
  preview?: boolean;
}) {
  const [viewing, setViewing] = useState<number | undefined>(undefined);

  if (attachments.length === 0) return null;

  if (preview) {
    return <PreviewStrip attachments={attachments} />;
  }

  const fill = width === 'fill';
  const images = attachments.filter((a) => a.kind !== 'link');
  const links = attachments.filter((a) => a.kind === 'link');
  const rows = imageRows(images.length);
  const starts = rows.map((_, i) => rows.slice(0, i).reduce((sum, r) => sum + r.count, 0));

  return (
    <YStack gap="$2">
      {images.length > 0 && (
        <YStack gap={GAP} width={fill ? '100%' : width} maxWidth={fill ? FILL_MAX : undefined}>
          {rows.map((row, rowIndex) => {
            const first = starts[rowIndex];
            const cells = images.slice(first, first + row.count);
            const cellWidth = fill ? '100%' : (width - GAP * (row.count - 1)) / row.count;

            return (
              <XStack key={rowIndex} gap={GAP}>
                {cells.map((image, cellIndex) => (
                  <Pressable
                    key={image.id}
                    accessibilityRole="button"
                    accessibilityLabel={photoCellLabel(first + cellIndex, images.length)}
                    onPress={() => setViewing(first + cellIndex)}
                    style={({ pressed, hovered, focused }: PressState) => [
                      { flex: fill ? 1 : undefined, opacity: pressed || hovered ? 0.9 : 1, borderRadius: radius.md },
                      focusRing(focused),
                    ]}
                  >
                    <AuthenticatedImage
                      path={thumbnailPath(image.url)}
                      accessibilityLabel="Attached photo"
                      width={cellWidth}
                      aspectRatio={row.aspect}
                      objectFit="cover"
                      borderRadius="$md"
                    />
                  </Pressable>
                ))}
              </XStack>
            );
          })}
        </YStack>
      )}

      {links.map((link) => (
        <LinkPreviewCard key={link.id} attachment={link} width={fill ? undefined : Math.max(width, 260)} />
      ))}

      {viewing !== undefined && (
        <Lightbox
          key={viewing}
          paths={images.map((image) => image.url)}
          initialIndex={viewing}
          onClose={() => setViewing(undefined)}
        />
      )}
    </YStack>
  );
}

// Full-size viewer, always on the warm dark of the dark theme so it stays
// inside the Nearkin palette instead of dropping to pure black. Pages
// horizontally (swipe, or the arrows -- a mouse has no swipe), shows where
// you are, and has a real close button apart from the backdrop so the
// photo itself is never announced as a control. The original is fetched
// here and nowhere else (the grid shows the 640px variant).
function Lightbox({ paths, initialIndex, onClose }: { paths: string[]; initialIndex: number; onClose: () => void }) {
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const listRef = useRef<FlatList<string>>(null);
  const [current, setCurrent] = useState(initialIndex);
  const frameHeight = height * 0.78;

  // After a resize or rotation the page width changes; keep the current photo
  // centred instead of sitting between two pages.
  useEffect(() => {
    listRef.current?.scrollToIndex({ index: current, animated: false });
    // Only the width changing should re-scroll; `current` follows the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const go = (next: number) => {
    listRef.current?.scrollToIndex({ index: next, animated: true });
    setCurrent(next);
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View flex={1} backgroundColor={darkColors.surfacePage}>
        {
          <FlatList
            ref={listRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            data={paths}
            keyExtractor={(path) => path}
            initialScrollIndex={initialIndex}
            getItemLayout={(_, i) => ({ length: width, offset: width * i, index: i })}
            onScroll={(event) => setCurrent(Math.round(event.nativeEvent.contentOffset.x / width))}
            scrollEventThrottle={16}
            style={{ flex: 1 }}
            contentContainerStyle={{ alignItems: 'center' }}
            renderItem={({ item, index: i }) => (
              <View width={width} alignItems="center" justifyContent="center">
                <AuthenticatedImage
                  path={item}
                  accessibilityLabel={`Photo ${i + 1} of ${paths.length}`}
                  width={width}
                  aspectRatio={width / frameHeight}
                  objectFit="contain"
                />
              </View>
            )}
          />
        }

        <XStack
          position="absolute"
          top={insets.top + 8}
          left={16}
          right={16}
          alignItems="center"
          justifyContent="space-between"
          pointerEvents="box-none"
        >
          <Text color={darkColors.textPrimary} fontSize="$meta" lineHeight="$meta">
            {paths.length > 1 ? positionLabel(current, paths.length) : ''}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close photo viewer"
            onPress={onClose}
            {...hit(8)}
            style={({ pressed, hovered, focused }: PressState) => [
              {
                width: TARGET,
                height: TARGET,
                borderRadius: radius.full,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: pressed || hovered ? darkColors.surfaceCard : 'transparent',
              },
              focusRing(focused),
            ]}
          >
            <X size={24} strokeWidth={1.75} color={darkColors.textPrimary} />
          </Pressable>
        </XStack>

        {paths.length > 1 && (
          <>
            {current > 0 && <PagerButton side="left" label="Previous photo" onPress={() => go(current - 1)} />}
            {current < paths.length - 1 && (
              <PagerButton side="right" label="Next photo" onPress={() => go(current + 1)} />
            )}
          </>
        )}
      </View>
    </Modal>
  );
}

function PagerButton({ side, label, onPress }: { side: 'left' | 'right'; label: string; onPress: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      {...hit(8)}
      style={({ pressed, hovered, focused }: PressState) => [
        {
          position: 'absolute',
          top: '50%',
          [side]: 12,
          width: TARGET,
          height: TARGET,
          marginTop: -TARGET / 2,
          borderRadius: radius.full,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: pressed || hovered ? darkColors.surfaceCard : 'transparent',
        },
        focusRing(focused),
      ]}
    >
      <Icon size={24} strokeWidth={1.75} color={darkColors.textPrimary} />
    </Pressable>
  );
}

function PreviewStrip({ attachments }: { attachments: Attachment[] }) {
  const first = attachments[0];
  const extra = attachments.length - 1;

  if (first.kind === 'link') {
    return (
      <YStack gap="$1">
        <LinkPreviewCard attachment={first} compact />
        {extra > 0 && (
          <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
            {extra === 1 ? '+1 more attachment' : `+${extra} more attachments`}
          </Text>
        )}
      </YStack>
    );
  }

  return (
    <View>
      <AuthenticatedImage
        path={thumbnailPath(first.url)}
        accessibilityLabel={extra > 0 ? `Attached photo, 1 of ${attachments.length}` : 'Attached photo'}
        width="100%"
        aspectRatio={2}
        objectFit="cover"
        borderRadius="$md"
      />
      {extra > 0 && (
        // A count over the photo says "there is more" where the eye already
        // is, instead of a grey line underneath that is easy to miss.
        <View
          position="absolute"
          right="$2"
          bottom="$2"
          backgroundColor={darkColors.surfacePage}
          borderRadius="$md"
          px="$2"
          py="$1"
        >
          <Text color={darkColors.textPrimary} fontSize="$meta" lineHeight="$meta" fontWeight="$semibold">
            {`+${extra}`}
          </Text>
        </View>
      )}
    </View>
  );
}
